import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import DISCBadge from '../components/DISCBadge';
import DOMPurify from 'dompurify';
import {
  ShieldCheck, Check, X, Edit3, RefreshCw, Loader2, ChevronDown,
  ChevronRight, Sparkles, Mail, AlertCircle, Clock, FileText, Eye,
  ThumbsUp, ThumbsDown, RotateCcw, Send, MessageSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QualityScoreBadge({ score }) {
  if (!score && score !== 0) return null;
  const color = score >= 8 ? 'text-green-700 bg-green-50 border-green-200'
    : score >= 6 ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-red-700 bg-red-50 border-red-200';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${color}`}>
      {score.toFixed(1)}
    </span>
  );
}

function ScoreBreakdown({ breakdown }) {
  if (!breakdown) return null;
  const dims = [
    { key: 'voice_match', label: 'Voice Match' },
    { key: 'disc_alignment', label: 'DISC Alignment' },
    { key: 'relevance', label: 'Relevance' },
    { key: 'clarity', label: 'Clarity' },
    { key: 'cta_strength', label: 'CTA Strength' },
  ];
  return (
    <div className="grid grid-cols-5 gap-2">
      {dims.map(d => (
        <div key={d.key} className="text-center">
          <div className="text-[10px] text-stone-400 mb-1">{d.label}</div>
          <div className={`text-sm font-bold ${
            (breakdown[d.key] || 0) >= 8 ? 'text-green-600'
            : (breakdown[d.key] || 0) >= 6 ? 'text-amber-600'
            : 'text-red-600'
          }`}>
            {breakdown[d.key] || '-'}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewCard({ review, onSelect, selected }) {
  const draft = review.generated_draft || {};
  const isSelected = selected?.review_id === review.review_id;

  return (
    <div
      onClick={() => onSelect(review)}
      className={`p-4 border rounded-xl cursor-pointer transition-all ${
        isSelected ? 'border-violet-400 bg-violet-50/50 shadow-md' : 'border-stone-200 hover:border-stone-300 hover:shadow-sm bg-white'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
            <Mail className="w-4 h-4 text-violet-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-stone-900">{review.contact_name || 'Unknown'}</p>
            <p className="text-[11px] text-stone-400">{review.contact_email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {review.disc_profile && <DISCBadge profile={review.disc_profile} size="sm" />}
          <QualityScoreBadge score={draft.quality_score} />
        </div>
      </div>

      <p className="text-xs font-medium text-stone-700 mb-1 truncate">{draft.subject || '(No subject)'}</p>
      <p className="text-[11px] text-stone-400 line-clamp-2">{(draft.body || '').replace(/<[^>]+>/g, '').substring(0, 120)}</p>

      <div className="flex items-center gap-3 mt-2 text-[10px] text-stone-400">
        <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />{review.campaign_name}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(review.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function ReviewDetail({ review, getAuthHeader, onAction, actionLoading }) {
  const [editMode, setEditMode] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [feedback, setFeedback] = useState('');
  const [regenGuidance, setRegenGuidance] = useState('');
  const [showRegen, setShowRegen] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');

  const draft = review.generated_draft || {};
  const context = review.context_snapshot || {};

  useEffect(() => {
    setEditSubject(draft.subject || '');
    setEditBody(draft.body || '');
    setEditMode(false);
    setFeedback('');
    setRegenGuidance('');
    setShowRegen(false);
    setActiveTab('preview');
  }, [review.review_id]);

  const contextTabs = [
    { key: 'preview', label: 'Email Preview', icon: Eye },
    { key: 'contact', label: 'Contact', icon: FileText },
    { key: 'account', label: 'Company', icon: FileText },
    { key: 'communication', label: 'History', icon: MessageSquare },
    { key: 'sender', label: 'Sender Voice', icon: Sparkles },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-stone-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-stone-900">Email to {review.contact_name}</h3>
            {review.disc_profile && <DISCBadge profile={review.disc_profile} size="sm" />}
          </div>
          <QualityScoreBadge score={draft.quality_score} />
        </div>
        <ScoreBreakdown breakdown={draft.quality_breakdown} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-3 border-b border-stone-100 flex-shrink-0">
        {contextTabs.map(tab => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${
                activeTab === tab.key
                  ? 'bg-white border border-b-0 border-stone-200 text-stone-900'
                  : 'text-stone-500 hover:text-stone-700 hover:bg-stone-50'
              }`}
            >
              <TabIcon className="w-3 h-3" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'preview' && (
          <div className="space-y-4">
            {/* Subject */}
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Subject</label>
              {editMode ? (
                <input
                  value={editSubject}
                  onChange={e => setEditSubject(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                />
              ) : (
                <p className="text-sm font-medium text-stone-900">{draft.subject}</p>
              )}
            </div>

            {/* Body */}
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Body</label>
              {editMode ? (
                <textarea
                  value={editBody}
                  onChange={e => setEditBody(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 font-mono"
                />
              ) : (
                <div className="bg-white border border-stone-200 rounded-lg p-4 text-sm text-stone-700 prose prose-sm max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(draft.body || '') }} />
                </div>
              )}
            </div>

            {/* Coaching Notes */}
            {draft.coaching_notes?.length > 0 && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-violet-700 mb-1.5">AI Coaching Notes</p>
                <ul className="space-y-1">
                  {draft.coaching_notes.map((note, i) => (
                    <li key={i} className="text-[11px] text-violet-600 flex items-start gap-1.5">
                      <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Regenerate section */}
            {showRegen && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-amber-700">Regenerate with guidance</p>
                <textarea
                  value={regenGuidance}
                  onChange={e => setRegenGuidance(e.target.value)}
                  placeholder="Tell the AI what to change... e.g. 'Make it shorter and more casual'"
                  rows={3}
                  className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowRegen(false)} className="text-xs">Cancel</Button>
                  <Button
                    size="sm"
                    onClick={() => onAction('regenerate', { guidance: regenGuidance })}
                    disabled={!regenGuidance.trim() || actionLoading}
                    className="text-xs bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {actionLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                    Regenerate
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <pre className="text-xs text-stone-700 whitespace-pre-wrap font-mono leading-relaxed">
              {context.contact_md || 'No contact context available.'}
            </pre>
          </div>
        )}

        {activeTab === 'account' && (
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <pre className="text-xs text-stone-700 whitespace-pre-wrap font-mono leading-relaxed">
              {context.account_md || 'No company context available.'}
            </pre>
          </div>
        )}

        {activeTab === 'communication' && (
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <pre className="text-xs text-stone-700 whitespace-pre-wrap font-mono leading-relaxed">
              {context.communication_md || 'No communication history available.'}
            </pre>
          </div>
        )}

        {activeTab === 'sender' && (
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <pre className="text-xs text-stone-700 whitespace-pre-wrap font-mono leading-relaxed">
              {context.sender_voice_md || 'No sender voice profile available.'}
            </pre>
          </div>
        )}
      </div>

      {/* Actions Footer */}
      <div className="px-6 py-4 border-t border-stone-200 flex-shrink-0 space-y-3">
        {/* Feedback for reject */}
        {!editMode && (
          <div>
            <textarea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder="Optional feedback for AI learning..."
              rows={2}
              className="w-full px-3 py-2 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
            />
          </div>
        )}

        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditMode(false)} className="flex-1 text-xs">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => onAction('edit', { subject: editSubject, body: editBody, feedback })}
                disabled={actionLoading}
                className="flex-1 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              >
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                Send Edited
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAction('reject', { feedback })}
                disabled={actionLoading}
                className="text-xs text-red-600 border-red-200 hover:bg-red-50"
              >
                <ThumbsDown className="w-3 h-3 mr-1" />
                Reject
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowRegen(!showRegen)}
                className="text-xs"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Regen
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditMode(true);
                  setEditSubject(draft.subject || '');
                  setEditBody(draft.body || '');
                }}
                className="text-xs"
              >
                <Edit3 className="w-3 h-3 mr-1" />
                Edit
              </Button>
              <Button
                size="sm"
                onClick={() => onAction('approve')}
                disabled={actionLoading}
                className="flex-1 text-xs bg-green-600 hover:bg-green-700 text-white"
              >
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ThumbsUp className="w-3 h-3 mr-1" />}
                Approve & Send
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AgenticReviews() {
  const { getAuthHeader } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');

  const fetchReviews = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/agentic-reviews${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/agentic-reviews/stats`, { headers: getAuthHeader() }),
      ]);
      setReviews(reviewsRes.data.data || []);
      setStats(statsRes.data || {});
    } catch (err) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, statusFilter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSelectReview = async (review) => {
    try {
      const res = await axios.get(`${API}/agentic-reviews/${review.review_id}`, { headers: getAuthHeader() });
      setSelectedReview(review);
      setSelectedDetail(res.data);
    } catch {
      toast.error('Failed to load review details');
    }
  };

  const handleAction = async (action, data = {}) => {
    if (!selectedDetail) return;
    setActionLoading(true);
    try {
      const reviewId = selectedDetail.review_id;
      if (action === 'approve') {
        await axios.post(`${API}/agentic-reviews/${reviewId}/approve`, {}, { headers: getAuthHeader() });
        toast.success('Email approved and sent!');
      } else if (action === 'edit') {
        await axios.post(`${API}/agentic-reviews/${reviewId}/edit`, data, { headers: getAuthHeader() });
        toast.success('Email edited and sent!');
      } else if (action === 'reject') {
        await axios.post(`${API}/agentic-reviews/${reviewId}/reject`, data, { headers: getAuthHeader() });
        toast.info('Draft rejected. Feedback recorded.');
      } else if (action === 'regenerate') {
        const res = await axios.post(`${API}/agentic-reviews/${reviewId}/regenerate`, data, { headers: getAuthHeader() });
        // Update the detail with new draft
        setSelectedDetail(prev => ({
          ...prev,
          generated_draft: {
            ...res.data.draft,
            quality_score: res.data.score?.quality_score,
            quality_breakdown: res.data.score?.quality_breakdown,
          },
        }));
        toast.success('Draft regenerated!');
        setActionLoading(false);
        return;
      }

      // Remove from list and clear selection
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
      setSelectedReview(null);
      setSelectedDetail(null);
      fetchReviews(); // Refresh stats
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-stone-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-stone-900">AI Review Queue</h1>
              <p className="text-xs text-stone-400">Review and approve AI-generated personalized emails</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Stats badges */}
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                {stats.pending || 0} pending
              </span>
              <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">
                {stats.approved_today || 0} approved today
              </span>
              {(stats.rejected_today || 0) > 0 && (
                <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200">
                  {stats.rejected_today} rejected
                </span>
              )}
            </div>

            <Button size="sm" variant="outline" onClick={fetchReviews} className="text-xs">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mt-3">
          {[
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'edited', label: 'Edited' },
            { value: 'rejected', label: 'Rejected' },
            { value: '', label: 'All' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                statusFilter === f.value
                  ? 'bg-stone-900 text-white'
                  : 'text-stone-500 hover:bg-stone-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Review list */}
        <div className={`${selectedDetail ? 'w-96' : 'w-full max-w-3xl mx-auto'} border-r border-stone-200 overflow-y-auto p-4 space-y-2 transition-all`}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-20">
              <ShieldCheck className="w-12 h-12 text-stone-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-stone-500">No reviews {statusFilter ? `with status "${statusFilter}"` : ''}</p>
              <p className="text-xs text-stone-400 mt-1">AI-generated email drafts will appear here for review</p>
            </div>
          ) : (
            reviews.map(review => (
              <ReviewCard
                key={review.review_id}
                review={review}
                selected={selectedReview}
                onSelect={handleSelectReview}
              />
            ))
          )}
        </div>

        {/* Detail panel */}
        {selectedDetail && (
          <div className="flex-1 bg-white overflow-hidden">
            <ReviewDetail
              review={selectedDetail}
              getAuthHeader={getAuthHeader}
              onAction={handleAction}
              actionLoading={actionLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
